import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { IconButton, makeStyles, Tooltip } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'

const useStyles = makeStyles(theme => ({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25)
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 'var(--text-weight-bold)',
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  row: {
    display: 'grid',
    gap: theme.spacing(0.5),
    alignItems: 'center'
  },
  filterRow: {
    gridTemplateColumns: '1fr 2fr 28px'
  },
  dateRow: {
    gridTemplateColumns: '1fr 1fr 1fr 28px'
  },
  input: {
    padding: '4px 8px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '11px',
    outline: 'none',
    backgroundColor: 'white',
    minWidth: 0,
    '&:focus': { borderColor: 'var(--color-primary-400)', boxShadow: '0 0 0 2px var(--color-primary-100)' }
  },
  iconBtn: {
    padding: 2,
    color: 'var(--color-neutral-300)',
    '&:hover': { color: 'var(--color-danger-600)' }
  },
  addBtn: {
    padding: 2,
    color: 'var(--color-neutral-400)',
    '&:hover': { color: 'var(--color-primary-600)' }
  },
  empty: {
    fontSize: '11px',
    color: 'var(--color-neutral-400)',
    fontStyle: 'italic',
    padding: theme.spacing(0.25, 0)
  }
}))

const newId = () => Math.random().toString(36).slice(2, 9)

function tryParseValue (raw) {
  if (raw === '') return ''
  try { return JSON.parse(raw) } catch { return raw }
}

// Order-insensitive: prevents phantom onChange emissions on key-ordering diffs.
function stableStringify (v) {
  if (v == null) return JSON.stringify(v)
  if (typeof v !== 'object') return JSON.stringify(v)
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`
  const keys = Object.keys(v).sort()
  return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify(v[k])}`).join(',')}}`
}

function toFilterRows (filters = {}) {
  return Object.entries(filters).map(([name, value]) => ({
    id: newId(),
    name,
    value: typeof value === 'string' ? value : JSON.stringify(value)
  }))
}

function toDateRows (dates = {}) {
  return Object.entries(dates).map(([name, range]) => ({
    id: newId(),
    name,
    startDate: range?.startDate ?? '',
    endDate: range?.endDate ?? ''
  }))
}

function deriveVariables (filterRows, dateRows) {
  const filters = filterRows.reduce((acc, r) => {
    if (r.name) acc[r.name] = tryParseValue(r.value)
    return acc
  }, {})
  const dates = dateRows.reduce((acc, r) => {
    if (r.name) acc[r.name] = { startDate: r.startDate, endDate: r.endDate }
    return acc
  }, {})
  return { filters, dates }
}

function VariablesForm ({ value, onChange }) {
  const classes = useStyles()
  const [filterRows, setFilterRows] = useState(() => toFilterRows(value?.filters))
  const [dateRows, setDateRows] = useState(() => toDateRows(value?.dates))

  const lastEmittedRef = useRef(null)
  const valueRef = useRef(value)
  useEffect(() => { valueRef.current = value }, [value])

  const derived = useMemo(() => deriveVariables(filterRows, dateRows), [filterRows, dateRows])

  useEffect(() => {
    const serialized = stableStringify(derived)
    if (serialized === lastEmittedRef.current) return
    if (serialized === stableStringify(valueRef.current)) {
      lastEmittedRef.current = serialized
      return
    }
    lastEmittedRef.current = serialized
    onChange(derived)
  }, [derived, onChange])

  // Reseed local rows on external `value` changes (Reset, version switch).
  useEffect(() => {
    const incoming = stableStringify(value)
    if (incoming === lastEmittedRef.current) return
    lastEmittedRef.current = incoming
    setFilterRows(toFilterRows(value?.filters))
    setDateRows(toDateRows(value?.dates))
  }, [value])

  const updateFilter = useCallback((id, field, val) => {
    setFilterRows(rows => rows.map(r => r.id === id ? { ...r, [field]: val } : r))
  }, [])

  const updateDate = useCallback((id, field, val) => {
    setDateRows(rows => rows.map(r => r.id === id ? { ...r, [field]: val } : r))
  }, [])

  return (
    <div className={classes.wrap}>
      <div className={classes.group}>
        <div className={classes.groupHeader}>
          <span>Filter Variables</span>
          <Tooltip title='Add filter variable' arrow>
            <IconButton size='small' className={classes.addBtn} onClick={() => setFilterRows(rs => [...rs, { id: newId(), name: '', value: '' }])}>
              <AddIcon style={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </div>
        {filterRows.length === 0 && <div className={classes.empty}>No filters</div>}
        {filterRows.map(row => (
          <div key={row.id} className={`${classes.row} ${classes.filterRow}`}>
            <input className={classes.input} placeholder='name' value={row.name} onChange={(e) => updateFilter(row.id, 'name', e.target.value)} />
            <input className={classes.input} placeholder='value' value={row.value} onChange={(e) => updateFilter(row.id, 'value', e.target.value)} />
            <IconButton size='small' className={classes.iconBtn} onClick={() => setFilterRows(rs => rs.filter(r => r.id !== row.id))}>
              <CloseIcon style={{ fontSize: 14 }} />
            </IconButton>
          </div>
        ))}
      </div>

      <div className={classes.group}>
        <div className={classes.groupHeader}>
          <span>Date Variables</span>
          <Tooltip title='Add date variable' arrow>
            <IconButton size='small' className={classes.addBtn} onClick={() => setDateRows(rs => [...rs, { id: newId(), name: '', startDate: '', endDate: '' }])}>
              <AddIcon style={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </div>
        {dateRows.length === 0 && <div className={classes.empty}>No date variables</div>}
        {dateRows.map(row => (
          <div key={row.id} className={`${classes.row} ${classes.dateRow}`}>
            <input className={classes.input} placeholder='name' value={row.name} onChange={(e) => updateDate(row.id, 'name', e.target.value)} />
            <input className={classes.input} placeholder='start' value={row.startDate} onChange={(e) => updateDate(row.id, 'startDate', e.target.value)} />
            <input className={classes.input} placeholder='end' value={row.endDate} onChange={(e) => updateDate(row.id, 'endDate', e.target.value)} />
            <IconButton size='small' className={classes.iconBtn} onClick={() => setDateRows(rs => rs.filter(r => r.id !== row.id))}>
              <CloseIcon style={{ fontSize: 14 }} />
            </IconButton>
          </div>
        ))}
      </div>
    </div>
  )
}

VariablesForm.propTypes = {
  value: PropTypes.shape({
    filters: PropTypes.object,
    dates: PropTypes.object
  }),
  onChange: PropTypes.func.isRequired
}

export default VariablesForm
